import { colors } from "@indeed/ifl-tokens";
import React from "react";
import { StyleSheet } from "react-native";

import { Box, Button, Pressable, ScrollView } from "../../../../../../../../mads";
import { Modal } from "../../../../../../../../mads/Modal";
import { Text } from "../../../../../../../../mads/Text";
import { useI18n } from "../../../../../../../../shared/I18n";
import Sentiment from "../../../../../../../../shared/util/salary/Sentiment";
import { MarkupWrapper } from "../MarkupWrapper";
import { QuestionText } from "../QuestionText";
import { stripMarkup } from "../utils/stripMarkup";
import { MinimumPayForm } from "./forms/MinimumPayForm";
import { SentimentForm } from "./forms/SentimentForm";
import type { CollectionModalProps } from "./index";
import { useCollectionModalController } from "./useCollectionModalController";

/**
 * Serializable modal props (no callbacks — those live on the context's
 * `collectionModalCallbacksRef`, consumed via {@link useCollectionModalController}).
 */
export type CollectionModalOverlayProps = Pick<
  CollectionModalProps,
  "title" | "question" | "editDisclaimer" | "inferenceMethod" | "showSalary" | "data"
> & {
  isOpen: boolean;
  /** Dismiss the overlay (provider clears its open-state). */
  onClose: () => void;
};

/**
 * Desktop (web) presentation of the Collection modal: a centered overlay dialog
 * rendered inline by `JMUIModalContextProvider`, instead of the mobile
 * full-screen navigation screen (`CollectionModal`). Mirrors the web
 * `js-match-insights-provider` CollectionModal — same forms/footer, presented as
 * a centered `Modal` rather than taking over the embedded RNVJ pane (RN-5815).
 */
export function CollectionModalOverlay({
  isOpen,
  onClose,
  title,
  question,
  editDisclaimer,
  inferenceMethod,
  showSalary,
  data,
}: CollectionModalOverlayProps) {
  const gt = useI18n();

  const {
    loading,
    hasSubmitted,
    modalData,
    setModalData,
    originalSentiment,
    footerButtons,
    handleSave,
    handleClose,
  } = useCollectionModalController({
    data,
    inferenceMethod,
    showSalary,
    closeModal: onClose,
  });

  return (
    <Modal
      visible={isOpen}
      transparent
      animationType="fade"
      onRequestClose={handleClose}
      accessibilityViewIsModal
      accessibilityLabel={title}
    >
      {/* Backdrop: tapping outside the card dismisses. */}
      <Pressable
        sx={styles.backdrop}
        onPress={handleClose}
        accessibilityRole="button"
        accessibilityLabel={gt.gettext("Close")}
      >
        {/* Card: capture presses so taps inside don't dismiss. */}
        <Pressable sx={styles.card} onPress={() => undefined}>
          <Box sx={styles.header}>
            <Text
              level={3}
              variant="contrast"
              numberOfLines={1}
              accessibilityRole="header"
              sx={styles.headerTitle}
            >
              {title}
            </Text>
            <Pressable
              onPress={handleClose}
              accessibilityRole="button"
              accessibilityLabel={gt.gettext("Close")}
              sx={styles.closeButton}
            >
              <Text level={3} variant="secondary">
                ✕
              </Text>
            </Pressable>
          </Box>

          <ScrollView sx={styles.body} contentContainerSx={styles.bodyContent}>
            <Box sx={{ marginBottom: 4 }}>
              <Text level={4} variant="contrast" data-testid="modal-question">
                <QuestionText>{question}</QuestionText>
              </Text>
              {editDisclaimer && originalSentiment === Sentiment.POSITIVE && (
                <Text level={2} variant="secondary">
                  <MarkupWrapper label={editDisclaimer} sx={{ fontWeight: "bold" }} />
                </Text>
              )}
            </Box>
            {showSalary ? (
              <MinimumPayForm
                value={modalData.salaryPreference}
                onChange={(s) => {
                  setModalData({ ...modalData, salaryPreference: s });
                }}
                showErrors={hasSubmitted}
              />
            ) : (
              <SentimentForm
                isLoading={loading}
                onChange={(d) => {
                  setModalData({ ...modalData, ...d });
                }}
                value={modalData}
                handleSave={handleSave}
                onClose={handleClose}
                accessibilityQuestionText={stripMarkup(question)}
              />
            )}
          </ScrollView>

          {footerButtons && footerButtons.length > 0 && (
            <Box sx={styles.footer}>
              {footerButtons.map((buttonProps, index) => {
                return (
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                  <Button key={index} {...buttonProps.props} sx={{ width: "100%" }}>
                    {}
                    {/* eslint-disable-next-line @typescript-eslint/no-unsafe-member-access */}
                    {buttonProps.label}
                  </Button>
                );
              })}
            </Box>
          )}
        </Pressable>
      </Pressable>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    alignItems: "center",
    padding: 24,
  },
  card: {
    backgroundColor: "white",
    borderRadius: 12,
    width: "100%",
    maxWidth: 480,
    maxHeight: "85%",
    overflow: "hidden",
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    borderBottomColor: colors.neutral[300],
    borderBottomWidth: 1,
    paddingVertical: 12,
    paddingHorizontal: 16,
  },
  headerTitle: {
    flexShrink: 1,
    marginRight: 8,
  },
  closeButton: {
    padding: 8,
  },
  body: {
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  bodyContent: {
    flexGrow: 1,
  },
  footer: {
    flexDirection: "column",
    alignItems: "stretch",
    borderTopColor: colors.neutral[300],
    borderTopWidth: 1,
    gap: 16,
    padding: 16,
    backgroundColor: "white",
  },
});

export default CollectionModalOverlay;
